import { Button, Card, Flex, Spin, Table, TableProps, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useGetRedemptionsQuery, useGetRedemptionsRawQuery, useLazyGetRedemptionsRawQuery } from "../../../api/redemption/redemptionApi";
import { Redemption } from "../../../api/redemption/Redemption";
import { useEffect, useMemo, useState } from "react";
import { useGetInventoryQuery } from "../../../api/inventory/inventoryApi";

/**
 * KYC Pending Submission Card
 * An order that has already been sent out (NOT editable any more) and has
 * not been marked as KYC submitted will be displayed here.
 */
export default function NewOrderCard() {
  const navigate = useNavigate();
  const { data, error, isLoading } = useGetRedemptionsRawQuery({});
  const [lazyGetRedemptions] = useLazyGetRedemptionsRawQuery();

  const { data: invData, error: invError, isLoading: invIsLoading } = useGetInventoryQuery();

  const [redemptionData, setRedemptionData] = useState<Redemption[]>();
  const [isFetchingExt, setIsFetchingExt] = useState(false);

  useEffect(() => {
    // if data changes then update the base data
    async function run() {
      const rd: Redemption[] = [];
      let lek = data?.LastEvaluatedKey;
      if (data?.Items) {
        rd.push(...data.Items);
      }
      while (lek != null) {
        setIsFetchingExt(true);
        console.log("loading more", lek);
        const more = await lazyGetRedemptions({
          lastEvaluatedKey: lek
        }).unwrap();
        if (more?.Items) {
          rd.push(...more.Items);
        }
        if (more?.LastEvaluatedKey) {
          lek = more.LastEvaluatedKey;
        } else {
          lek = undefined;
          setIsFetchingExt(false);
        }
      }
      const filtered = rd.filter(r => {
        // Exclude 3UK
        if (r.InternalSku.includes("-3uk-")) {
          return false;
        }
        return true;
      });
      setRedemptionData(filtered);
      console.log("redemptionData updated:", rd);
    }
    run();
  }, [data, lazyGetRedemptions]);

  const filteredData = useMemo(() => {
    const ret: Redemption[] = [];
    if (!redemptionData || !invData) {
      return ret;
    }
    const items = redemptionData;


    for (const r of items) {
      if (r.InternalSku.startsWith("joy-")) {
        console.log("skipping joy sku", r.InternalSku);
        continue;
      }

      if (r.InternalSku.startsWith("tgt-")) {
        console.log("skipping tgt sku", r.InternalSku);
        continue;
      }

      if (r.IsCanceled) {
        console.log("skipping canceled sku", r.InternalSku);
        continue;
      }

      if (r.InternalSku.includes("-3uk-")) {
        console.log("skipping esim uk 3uk sku", r.InternalSku);
        continue;
      }

      if (r.InternalSku.startsWith("sb-esim-att-ultra60")) {
        console.log("skipping static assigned sku", r.InternalSku);
        continue;
      }

      // New Order Section (SN is not assigned yet as admin has not clicked on it)
      // find matching record in invData
      if (invData?.some(inv =>
        (inv.RedemptionOrderId === r.OrderId
         && inv.RedemptionSimIdx === r.SimIdx)
        || (inv.ReservedOrderId === r.OrderId
            && inv.ReservedSimIdx === r.SimIdx)
      )) {
        console.log("skipping for new orders", r);
        continue;
      }


      if (r.Kyc != null && r.KycSubmittedBy == null) {
        // todo: figure out why IsManualQr is set on all of them?
        ret.push(r);
      }
    }
    console.log("ret count", ret.length, redemptionData.length);
    return ret;
  }, [redemptionData, invData]);

  console.log("filtered data for pending submission card", filteredData);
  console.log("original data", data);

  const columns: TableProps<Redemption>["columns"] = [
    {
      title: "Order ID",
      dataIndex: "OrderId",
      key: "OrderId",
    },
    {
      title: "#",
      dataIndex: "SimIdx",
      key: "SimIdx",
    },
    {
      title: "SIMPLY Product SKU",
      dataIndex: "InternalSku",
      key: "InternalSku",
    },
    {
      title: "Submitted At",
      dataIndex: "RedeemedAt",
      key: "RedeemedAt",
      render: (val) => val != null
                       ? dayjs(val).add(8, "hour").format("DD-MMM-YYYY HH:mm")
                       : "-"
    },
    // {
    //   title: "KYC Status",
    //   dataIndex: "kycSubmittedBy",
    //   key: "kycSubmittedBy",
    //   render: (user: string) => (user ? "Submitted" : "Pending"),
    // },
    {
      title: "Arrival Date",
      dataIndex: "arrivalDate",
      key: "arrivalDate",
      defaultSortOrder: "ascend",
      sorter: (a, b) => {
        const dateA = a.Kyc?.arrivalDate ? dayjs(a.Kyc.arrivalDate) : dayjs(0);
        const dateB = b.Kyc?.arrivalDate ? dayjs(b.Kyc.arrivalDate) : dayjs(0);
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      },
      render: (_, rec) => dayjs(rec.Kyc?.arrivalDate).format("DD-MMM-YYYY")
    },
    {
      title: "Action",
      render: (_, record: Redemption) => (
        <>
          <Button
            type="primary"
            // disabled={!record.assignedSimSn}
            onClick={() => {
                return navigate(`/redemption?reference=${record.OrderId},${record.SimIdx}`);
              }
            }
          >
            View
          </Button>
        </>
      ),
    },
  ];

  console.log("pending submission data", filteredData);
  if (error){
  console.log("error", error);
  }

  return (
    <Card
      title="New Orders"
      style={{ width: "100%", height: "100%" }}
    >
      {(isLoading || isFetchingExt) && (
        <Flex justify="center" align="center" style={{ height: "100%" }}>
          <Spin />
        </Flex>
      )}
      {error && (
        <Typography.Text>Error loading inventory status</Typography.Text>
      )}
      {data && (
        <Table<Redemption>
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={false}
          size="small"
        />
      )}
    </Card>
  );
}
